import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { finalize } from 'rxjs';
import { DealsApiService } from '@/pages/deals/data-access/deals-api.service';
import { Deal } from '@/pages/deals/data-access/deals.model';
import { assetClasses, getSubAssetClassLabelKey, investmentTypes } from '@/pages/deals/data-access/deals.option';
import { DealsFilterRM } from '@/pages/deals/data-access/deals.enum';
import { HelpersService } from '@/shared/services/helpers.service';
import { PaginatedGridComponent } from '@/shared/ui-components/paginated-grid/paginated-grid.component';
import { CardComponent } from '@/shared/ui-components/card/card.component';
import { TagComponent } from '@/shared/ui-components/tag/tag.component';
import { ProgressBarComponent } from '@/shared/ui-components/progress-bar/progress-bar.component';
import { DividerComponent } from '@/shared/ui-components/divider/divider.component';
import { CurrencyDisplayComponent } from '@/shared/ui-components/currency-display/currency-display.component';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { TagSeverity } from '@/shared/enums/generic.enum';
import { PaginatedResponse } from '@/shared/models/paginated-response.model';

const PAGE_SIZE = 2;

@Component({
    selector: 'app-rm-live-deals',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        TranslateModule,
        SvgIconComponent,
        PaginatedGridComponent,
        CardComponent,
        TagComponent,
        ProgressBarComponent,
        DividerComponent,
        CurrencyDisplayComponent,
        ButtonComponent,
    ],
    templateUrl: './rm-live-deals.component.html',
})
export class RmLiveDealsComponent implements OnInit {
    private readonly _router = inject(Router);
    private readonly _dealsApi = inject(DealsApiService);
    private readonly _helpers = inject(HelpersService);
    private readonly _translate = inject(TranslateService);

    public readonly dealsData = signal<PaginatedResponse<Deal> | null>(null);
    public readonly loading = signal(false);

    public ngOnInit(): void {
        this.loadDeals(1);
    }

    public dealsItems(): Deal[] {
        return this.dealsData()?.items ?? [];
    }

    public dealsTotal(): number {
        return this.dealsData()?.total ?? 0;
    }

    public dealsFirst(): number {
        const d = this.dealsData();
        if (!d) return 0;
        const pageSize = d.pageSize || PAGE_SIZE;
        return Math.max(0, ((d.page || 1) - 1) * pageSize);
    }

    public dealsPageSize(): number {
        return this.dealsData()?.pageSize ?? PAGE_SIZE;
    }

    public showViewAll(): boolean {
        return this.dealsTotal() > 0;
    }

    public onViewAll(): void {
        void this._router.navigateByUrl('/rm/deal-management/deals/live');
    }

    public onPageChange(event: { first?: number; rows?: number }): void {
        const rows = event.rows ?? PAGE_SIZE;
        const first = event.first ?? 0;
        const page = Math.floor(first / rows) + 1;
        this.loadDeals(page, rows);
    }

    public getEmptyMessage(): string {
        return this._translate.instant('RM.DASHBOARD.LIVE_DEALS.EMPTY');
    }

    public getAssetClassIcon(assetClass: number | undefined): string {
        const icons: Record<number, string> = {
            0: '/icons/private_equity.svg',
            1: '/icons/private_credit.svg',
            2: '/icons/digital_assets.svg',
            3: '/icons/real_estate.svg',
            4: '/icons/commodities.svg',
            5: '/icons/collectibles.svg',
        };
        return icons[assetClass ?? 0] ?? '/icons/private_credit.svg';
    }

    public getCategoriesFromDeal(d: Deal): string[] {
        const parts: string[] = [];
        if (d.assetClass != null) {
            const ac = assetClasses.find((o) => o.value === d.assetClass);
            parts.push(ac ? this._translate.instant(ac.labelKey) : '—');
        }
        if (d.assetSubClass != null) {
            const subLabelKey = getSubAssetClassLabelKey(d.assetSubClass);
            parts.push(subLabelKey ? this._translate.instant(subLabelKey) : '—');
        }
        if (d.investmentType != null) {
            const it = investmentTypes.find((o) => o.value === d.investmentType);
            parts.push(it ? this._translate.instant(it.labelKey) : '—');
        }
        return parts.length ? parts : ['—'];
    }

    public getDealTag(d: Deal): string {
        const closeDate = d.closeDate;
        if (closeDate) {
            const d2 = typeof closeDate === 'string' ? new Date(closeDate) : closeDate;
            if (!isNaN(d2.getTime())) {
                const day = String(d2.getDate()).padStart(2, '0');
                const month = String(d2.getMonth() + 1).padStart(2, '0');
                const year = d2.getFullYear();
                return `Closing on ${day}/${month}/${year}`;
            }
        }
        return 'New';
    }

    public getDealTagSeverity(d: Deal): TagSeverity {
        const closeDate = d.closeDate;
        if (closeDate) {
            const d2 = typeof closeDate === 'string' ? new Date(closeDate) : closeDate;
            if (!isNaN(d2.getTime())) {
                const daysUntilClose = Math.ceil((d2.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                if (daysUntilClose <= 14) return TagSeverity.HELP;
            }
        }
        return TagSeverity.PRIMARY;
    }

    public getDealProgressValue(d: Deal): number {
        return (d as Deal & { clientDealPerformance?: { progressPercent?: number } }).clientDealPerformance
            ?.progressPercent ?? 0;
    }

    public getDealRemainingAllocation(d: Deal): number {
        const remaining = Math.max(0, 100 - this.getDealProgressValue(d));
        return Math.round(remaining * 100) / 100;
    }

    public getDealDetailsLink(d: Deal): unknown[] {
        const rmDealId = d.relationshipManagerDealId ?? d.id;
        return ['/rm', 'deal-details', rmDealId];
    }

    private loadDeals(page = 1, pageSize = PAGE_SIZE): void {
        this.loading.set(true);
        this._dealsApi
            .getRmDeals(page, pageSize, {
                WealthManagerApprovalStatus: DealsFilterRM.LIVE,
            })
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (res) => this.dealsData.set(res),
                error: (err) => {
                    this._helpers.showErrorMessage(err);
                    this.dealsData.set({ items: [], total: 0, page, pageSize });
                },
            });
    }
}
